<template>
  <el-dialog
    :visible.sync="isShow"
    :show-close="false"
    :close-on-click-modal="false"
    append-to-body
    destroy-on-close
    custom-class="popup-component"
    @close="closePopup"
  >
    <div class="popup-content">
      <button type="button" class="popup-close" aria-label="Close" @click="closePopup">
        <i class="el-icon-close" />
      </button>
      <div class="popup-left">
        <div v-if="title" class="popup-title" v-text="title" />
        <div v-if="content" class="popup-text" v-text="content" />
        <el-form
          ref="emailForm"
          :model="emailModel"
          :rules="emailRules"
          @submit.native.prevent="onSubmit"
        >
          <el-form-item prop="email" class="popup-form-item">
            <el-input
              v-model="emailValue"
              :placeholder="inputPlaceholder"
              class="popup-input"
            />
          </el-form-item>
        </el-form>
        <div
          v-if="showCheckbox"
          class="popup-checkbox"
          role="checkbox"
          :aria-checked="checkboxChecked"
          tabindex="0"
          @click="checkboxChecked = !checkboxChecked"
          @keydown.space.prevent="checkboxChecked = !checkboxChecked"
        >
          <span class="popup-checkbox-box" :class="{ 'is-checked': checkboxChecked }">
            <i v-if="checkboxChecked" class="iconfont icon-success" />
          </span>
          <span class="popup-checkbox-label">{{ complianceText }}</span>
        </div>
        <div class="popup-spacer" />
        <el-button
          :loading="loading"
          :disabled="showCheckbox && !checkboxChecked"
          class="popup-btn"
          @click="onSubmit"
        >
          {{ buttonText }}
        </el-button>
      </div>
      <div v-if="imageUrl" class="popup-right">
        <img v-lazy-load :data-src="imageUrl" :alt="imageAlt" class="popup-image">
      </div>
    </div>
  </el-dialog>
</template>

<script>
import mixin from './mixin'

const EMAIL_RE = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,8})$/

export default {
  name: 'Popup',
  mixins: [mixin],
  computed: {
    // el-form 的 model 用，把 emailValue 包成对象供 el-form-item 校验取值
    emailModel() {
      return { email: this.emailValue }
    },
    // 邮箱校验规则：必填 + 正则格式，blur 时触发，错误走 el-form-item 内联红边框
    emailRules() {
      return {
        email: [
          { required: true, message: 'Please enter your email', trigger: 'blur' },
          { pattern: EMAIL_RE, message: 'Please enter a valid email', trigger: 'blur' },
        ],
      }
    },
  },
  methods: {
    // 提交按钮：先跑 el-form 校验（失败由内联红框提示），通过后再校验复选框、GA 埋点、调 save 提交
    onSubmit() {
      this.$refs.emailForm.validate((valid) => {
        if (!valid) return
        if (this.showCheckbox && !this.checkboxChecked) {
          this.$ElMessage({ type: 'warning', message: this.privacyWarn })
          return
        }
        this.GAReport()
        this.save({
          successCallback: () => {
            this.$ElMessage({ type: 'success', message: this.successNotice })
          },
          failCallback: (msg) => {
            this.$ElMessage({ type: 'warning', message: msg || 'Submit failed' })
          },
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$brand-orange: #ffaa0a;

.popup-content {
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;

  ::v-deep .el-input__inner,
  ::v-deep .el-checkbox__label,
  ::v-deep .el-button {
    font-family: 'Poppins', sans-serif;
  }
}

.popup-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  color: #7e868a;
  font-size: 32px;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;

  // &:hover {
  //   color: #fff;
  //   transform: rotate(90deg);
  // }
}

.popup-left {
  position: relative;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 32px 28px 32px 32px;
  text-align: left;
  overflow-x: hidden;
  overflow-y: auto;
}

.popup-spacer {
  flex: 1 1 24px;
  min-height: 24px;
}

.popup-right {
  flex: 0 0 260px;
  overflow: hidden;
  background: #f5f5f5;
}

.popup-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 260px;
  max-height: 510px;
  object-fit: cover;
  object-position: center;
}

.popup-title {
  color: #000000d9;
  font-size: 36px;
  font-weight: 700;
  line-height: 45px;
  word-break: normal;
  overflow-wrap: break-word;
}

.popup-text {
  margin-top: 16px;
  color: #000000d9;
  font-size: 14px;
  word-break: normal;
  overflow-wrap: break-word;
}

.popup-input {
  margin-top: 20px;

  ::v-deep .el-input__inner {
    height: 40px;
    border: 1px solid #0003;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;

    &::placeholder {
      color: #0006;
      font-weight: 500;
    }

    &:focus {
      border-color: $brand-orange;
    }
  }
}

.popup-form-item {
  margin-bottom: 0;
}

::v-deep .el-form-item.is-error .el-input__inner {
  border-color: #f56c6c;

  &:focus {
    border-color: #f56c6c;
  }
}

.popup-checkbox {
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  min-width: 0;
  cursor: pointer;
  user-select: none;
  text-align: left;

  &:focus {
    outline: none;
  }

  .popup-checkbox-box {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-right: 10px;
    border-radius: 4px;
    background: #0000000d;
    border: 0.85px solid #0003;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, border-color 0.2s ease;

    .iconfont {
      color: #fff;
      font-size: 12px;
      line-height: 1;
    }

    &.is-checked {
      background: $brand-orange;
      border-color: $brand-orange;
    }
  }

  .popup-checkbox-label {
    flex: 1;
    min-width: 0;
    color: #0006;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    word-break: normal;
    overflow-wrap: break-word;
  }
}

.popup-btn {
  flex-shrink: 0;
  width: 100%;
  height: 52px;
  background: linear-gradient(90deg, #ffad08 0%, #ff5a00 100%);
  border: none;
  border-radius: 50px;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  transition: filter 0.2s ease, opacity 0.2s ease;

  &:hover,
  &:focus {
    background: linear-gradient(90deg, #ffad08 0%, #ff5a00 100%);
    filter: brightness(1.05);
    color: #fff;
  }

  &.is-loading {
    opacity: 0.85;
  }

  &.is-disabled,
  &.is-disabled:hover,
  &.is-disabled:focus {
    opacity: 0.4;
    cursor: not-allowed;
    background: linear-gradient(90deg, #ffad08 0%, #ff5a00 100%);
    color: #fff;
    filter: none;
  }
}
</style>

<style lang="scss">
.popup-component {
  display: flex;
  flex-direction: column;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  width: 700px !important;
  height: 510px !important;
  max-width: calc(100vw - 32px);
  margin: 0 auto !important;
  background: #fff;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    flex: 1;
    padding: 0;
    color: inherit;
    overflow: hidden;
  }
}
</style>
